import { useEffect } from "react";
import { CaseStudyLayout } from "@/components/case-studies/CaseStudyLayout";
import { LazyImage } from "@/components/ui/lazy-image";
import { caseStudies } from "@/data/caseStudyData";
import { Container } from "@/components/ui/container";
import { CtaSection } from "@/components/ui/cta-section";
import { RedactedCompanyName } from "@/components/case-studies/RedactedCompanyName";

const LegalFirmPracticeManagement = () => {
  // Find the case study data
  const caseStudy = caseStudies.find(study => study.id === "legal-firm-practice-management");
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  if (!caseStudy) {
    return (
      <Container className="pt-24 pb-16">
        <div className="text-center py-20">
          <h1 className="text-2xl font-bold text-gray-900">Case Study Not Found</h1>
          <p className="mt-4 text-gray-600">The case study you're looking for could not be found.</p>
        </div>
      </Container>
    );
  }

  return (
    <CaseStudyLayout caseStudy={caseStudy}>
      <article className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 md:p-8 lg:p-10">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-edgefi-purple/10 text-edgefi-purple text-sm font-medium rounded-full">
                  {caseStudy.industry}
                </span>
                {caseStudy.companyType && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                    {caseStudy.companyType}
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{caseStudy.title}</h1>
              {caseStudy.redactedName && (
                <RedactedCompanyName companyName={caseStudy.redactedName} />
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-10">
            <div className="md:col-span-2">
              <div className="prose prose-lg max-w-none">
                <h2 className="text-xl font-semibold text-gray-900">Challenge</h2>
                <p className="text-gray-700">{caseStudy.challenge}</p>
              </div>
            </div>
            
            <div className="md:col-span-1 bg-gray-50 p-5 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Results at a Glance</h3>
              <ul className="space-y-2">
                {caseStudy.results.map((result, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      className="text-green-500 mt-1 shrink-0"
                    >
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                    <span className="text-gray-700">{result.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Solution</h2>
            <p className="text-gray-700">{caseStudy.solution}</p>
          </div>

          <div className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Implementation</h2>
            <p className="text-gray-700">
              The team at edgefi implemented a comprehensive managed IT solution tailored to the legal firm's unique needs:
            </p>
            <ul className="mt-4 space-y-4">
              <li className="flex items-start gap-3">
                <div className="bg-edgefi-purple text-white rounded-full p-2 shrink-0 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m22 16.92-3.6-3.26a2.3 2.3 0 0 0-3.29.24l-2.05 2.79a17.6 17.6 0 0 1-5-5l2.79-2.06a2.3 2.3 0 0 0 .24-3.3L7.6 2.8a2.3 2.3 0 0 0-3.3-.24l-2.6 2.53a2.3 2.3 0 0 0-.6 1.84A19.2 19.2 0 0 0 20.54 22.4a2.3 2.3 0 0 0 1.84-.6l2.53-2.59a2.3 2.3 0 0 0-.24-3.3Z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">24/7 Proactive IT Monitoring and Support</h3>
                  <p className="text-gray-700">Implemented real-time monitoring systems to identify and resolve issues before they impact operations, with specialized support for legal practice management software.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-edgefi-purple text-white rounded-full p-2 shrink-0 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="m16 10-4.5 4.5L8 12"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Secure Document Management System</h3>
                  <p className="text-gray-700">Deployed an encrypted, cloud-based document management solution that integrates with existing case management software while ensuring client confidentiality and compliance with legal data regulations.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-edgefi-purple text-white rounded-full p-2 shrink-0 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Remote Work Infrastructure</h3>
                  <p className="text-gray-700">Established secure VPN connections, multi-factor authentication, and virtual desktop infrastructure to enable attorneys and staff to work securely from any location with the same access to resources as if in the office.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-edgefi-purple text-white rounded-full p-2 shrink-0 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Comprehensive Security and Compliance</h3>
                  <p className="text-gray-700">Implemented layered security solutions including endpoint protection, email security, network monitoring, and data loss prevention specifically configured to meet American Bar Association cybersecurity guidelines.</p>
                </div>
              </li>
            </ul>
          </div>

          {caseStudy.testimonial && (
            <div className="bg-gradient-to-r from-edgefi-purple/10 to-edgefi-purple/5 p-6 rounded-xl mb-10">
              <blockquote className="text-lg text-gray-700 italic relative">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-12 w-12 text-edgefi-purple/20 absolute top-0 left-0 -translate-x-4 -translate-y-4"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M10 7L8 11L11 15H9L6 11L8 7H10M16 7L14 11L17 15H15L12 11L14 7H16Z"
                  />
                </svg>
                <p className="relative z-10">{caseStudy.testimonial.quote}</p>
                <footer className="mt-4 font-medium">
                  <span className="text-edgefi-purple">{caseStudy.testimonial.author}</span>
                  <span className="text-gray-500 ml-1">— {caseStudy.testimonial.role}</span>
                </footer>
              </blockquote>
            </div>
          )}

          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Long-Term Impact</h2>
            <p className="text-gray-700">
              Beyond the immediate operational improvements, the legal firm experienced significant long-term benefits:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-gray-50 p-5 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">Increased Billable Hours</h3>
                <p className="text-gray-700">The elimination of IT-related downtime and simplified technology workflows allowed attorneys to reclaim nearly 350 billable hours per attorney annually, directly impacting the firm's revenue.</p>
              </div>
              <div className="bg-gray-50 p-5 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">Enhanced Client Experience</h3>
                <p className="text-gray-700">The secure client portal and document sharing capabilities improved client communication and satisfaction, leading to higher client retention rates and referrals.</p>
              </div>
            </div>
          </div>
        </div>
      </article>

      <div className="mt-12">
        <CtaSection
          title="Transform Your Legal Practice with Optimized IT"
          description="Discover how our managed services can help your firm eliminate technology frustrations and focus on practicing law."
          buttonText="Schedule a Consultation"
          buttonVariant="cta"
          bgColor="gradient"
          modalTitle="Legal IT Consultation"
          modalDescription="Tell us about your firm's IT challenges and we'll show you how we can help."
        />
      </div>
    </CaseStudyLayout>
  );
};

export default LegalFirmPracticeManagement;
